import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaCloudSun, FaThermometerHalf, FaWind, FaTint, FaSync, FaLocationArrow } from 'react-icons/fa';
import { WiShowers } from 'react-icons/wi';
import { weatherAPI } from '../api/api';
import { WEATHER_CONFIG, renderWeatherIcon, formatDay, getHungarianWeatherDescription } from '../config/weather';
import WeatherAlertsWidget from './WeatherAlertsWidget';
import '../css/WeatherWidget.css';

const WeatherWidget = ({
  coordinates = null,
  showLocation = true,
  showForecast = true,
  showAlerts = true,
  compact = false,
  className = '',
  onWeatherUpdate = null
}) => {
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Cache mechanizmus - statikus változók a komponensen kívül
  const getCacheKey = (coords) => `${coords.lat.toFixed(2)}_${coords.lon.toFixed(2)}`;
  const isDataFresh = (timestamp) => {
    const now = Date.now();
    const thirtyMinutes = 30 * 60 * 1000; // 30 perc
    return now - timestamp < thirtyMinutes;
  };

  
  const effectiveCoordinates = coordinates || userLocation;

  useEffect(() => {
    if (!coordinates) {
      requestLocationPermission();
    }
  }, [coordinates]);

  
  useEffect(() => {
    if (effectiveCoordinates) {
      fetchWeatherData(effectiveCoordinates);

      const intervalId = setInterval(() => {
        fetchWeatherData(effectiveCoordinates);
      }, 3600000); // 1 óra
      
      return () => clearInterval(intervalId);
    }
  }, [effectiveCoordinates]);

  const requestLocationPermission = async () => {
    try {
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported');
        setLocationPermission('denied');
        return;
      }

      const permission = await navigator.permissions.query({ name: 'geolocation' });
      setLocationPermission(permission.state);

      if (permission.state === 'granted') {
        getCurrentLocation();
      } else if (permission.state === 'prompt') {
        getCurrentLocation();
      } else {
        console.warn('Location permission denied');
        setLocationPermission('denied');
      }
    } catch (error) {
      console.error('Error checking location permission:', error);
      setLocationPermission('denied');
    }
  };

  const getCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lon: position.coords.longitude
        };
        setUserLocation(coords);
        fetchWeatherData(coords);
      },
      (error) => {
        console.error('Error getting location:', error);
        setLocationPermission('denied');
        fetchWeatherData(WEATHER_CONFIG.DEFAULT_COORDINATES);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const fetchWeatherData = async (coords, forceRefresh = false) => {
    const cacheKey = getCacheKey(coords);
    const cachedData = JSON.parse(localStorage.getItem(`weather_${cacheKey}`) || 'null');
    const cachedTimestamp = parseInt(localStorage.getItem(`weather_timestamp_${cacheKey}`) || '0');

    // Ha van friss cache adat és nem kényszerítjük a frissítést, használjuk azt
    if (!forceRefresh && cachedData && isDataFresh(cachedTimestamp)) {
      setWeatherData(cachedData);
      setLastUpdated(new Date(cachedTimestamp));
      return;
    }

    setWeatherLoading(true);
    setWeatherError(null);
    setIsRefreshing(true);

    try {
      const data = await weatherAPI.getWeatherData(coords.lat, coords.lon);  
       
      if (data && data.current && data.location && Array.isArray(data.forecast)) {
        const now = Date.now();
        setWeatherData(data);
        setLastUpdated(new Date(now));

        // Cache-elés
        localStorage.setItem(`weather_${cacheKey}`, JSON.stringify(data));
        localStorage.setItem(`weather_timestamp_${cacheKey}`, now.toString());

        if (onWeatherUpdate) {
          onWeatherUpdate(data);
        }
      } else {
        throw new Error('Invalid weather data structure received');
      }
    } catch (error) {
      // Ha a szerver nem elérhető, ne logoljunk hibát minden alkalommal
      if (error.message === 'Failed to fetch') {
        // Csendes hiba - szerver valószínűleg nem fut
        setWeatherError(null);
        setWeatherData(null);
      } else {
        console.error('Weather fetch error:', error);
        setWeatherError('Időjárás adatok betöltése sikertelen');
        setWeatherData(null);
      }
    } finally {
      setWeatherLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    if (effectiveCoordinates) {
      fetchWeatherData(effectiveCoordinates, true); // Force refresh
    }
  };

  const getLocationDisplay = () => {
    if (weatherData && weatherData.location) {
      return `${weatherData.location.name}, ${weatherData.location.country}`;
    } else if (locationPermission === 'granted' && userLocation) {
      return 'Jelenlegi hely';
    } else if (locationPermission === 'denied') {
      return 'Helyadat hozzáférés megtagadva';
    } else {
      return 'Helyadat betöltése...';
    }
  };

  if (compact) {
    return (
      <div className={`weather-widget-compact ${className}`}>
        {weatherLoading ? (
          <div className="weather-widget-loading-compact">
            <div className="weather-spinner-compact"></div>
          </div>
        ) : weatherError ? (
          <div className="weather-widget-error-compact">
            <FaCloudSun />
          </div>
        ) : weatherData && weatherData.current ? (
          <div className="weather-widget-compact-content">
            <div className="weather-temp-compact">
              {weatherData.current.temp}°
            </div>
            <div className="weather-icon-compact">
              {renderWeatherIcon(weatherData.current.description, weatherData.current.icon, '', 20)}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {showAlerts && effectiveCoordinates && (
        <WeatherAlertsWidget
          coordinates={effectiveCoordinates}
          showNotifications={true}
        />
      )}
      <div className={`weather-widget ${className}`}>
        <div className="weather-widget-header">
        <div className="weather-widget-title">
          <FaCloudSun className="weather-title-icon" />
          <h4>Időjárás</h4>
        </div>
        <div className="weather-widget-actions">
          <button 
            className="weather-refresh-btn"
            onClick={handleRefresh}
            disabled={weatherLoading || isRefreshing}
            title="Frissítés"
          >
            <FaSync className={isRefreshing ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {showLocation && (
        <div className="weather-widget-location">
          <FaMapMarkerAlt className="location-icon" />
          <span className="location-text">{getLocationDisplay()}</span>
        </div>
      )}

      {weatherLoading ? (
        <div className="weather-widget-loading">
          <div className="weather-spinner"></div>
          <span>Időjárás betöltése...</span>
        </div>
      ) : locationPermission === 'denied' && !coordinates ? (
        <div className="weather-widget-error">
          <FaCloudSun className="error-icon" />
          <span>Helyadat hozzáférés szükséges az időjárás megjelenítéséhez</span>
          <button onClick={requestLocationPermission} className="weather-retry-btn">
            Helyadat engedélyezése
          </button>
        </div>
      ) : weatherError ? (
        <div className="weather-widget-error">
          <FaCloudSun className="error-icon" />
          <span>{weatherError}</span>
          <button 
            onClick={() => fetchWeatherData(effectiveCoordinates || WEATHER_CONFIG.DEFAULT_COORDINATES)} 
            className="weather-retry-btn"
          >
            Újra próbál
          </button>
        </div>
      ) : weatherData && weatherData.current && weatherData.location ? (
        <>
          <div className="weather-widget-main">
            <div className="weather-widget-current">
              <div className="weather-temp-weather-group">
                <div className="weather-temp-large">
                  <FaThermometerHalf className="temp-icon" />
                  <span className="temp-value">{weatherData.current.temp}°C</span>
                </div>
                <div className="weather-description">
                  <span className="weather-icon-large">
                    {renderWeatherIcon(weatherData.current.description, weatherData.current.icon, '', 48)}
                  </span>
                  <span className="weather-text">
                    {getHungarianWeatherDescription(weatherData.current.description)}
                  </span>
                </div>
              </div>
              <div className="weather-details-inline">
                <div className="weather-detail-inline">
                  <FaWind className="detail-icon-inline" />
                  <span className="detail-value-inline">{weatherData.current.wind_speed} km/h</span>
                </div>
                <div className="weather-detail-inline">
                  <FaTint className="detail-icon-inline" />
                  <span className="detail-value-inline">{weatherData.current.humidity}%</span>
                </div>
              </div>
            </div>
          </div>

          {showForecast && weatherData.forecast && (
            <div className="weather-widget-forecast">
              <h5 className="forecast-title">Heti előrejelzés</h5>
              <div className="forecast-grid">
                {weatherData.forecast.map((day, i) => (
                  <div key={i} className="weather-forecast-item">
                    <div className="forecast-day">{formatDay(day.date)}</div>
                    <div className="forecast-icon">
                      {renderWeatherIcon(day.description, day.icon, '', 24)}
                    </div>
                    <div className="forecast-temp">
                      <span className="temp-min">{day.temp_min}°</span>
                      <span className="temp-separator">-</span>
                      <span className="temp-max">{day.temp_max}°C</span>
                    </div>
                    <div className="forecast-desc">
                      {getHungarianWeatherDescription(day.description)}
                    </div>
                    {day.precipitation_probability > 0 && (
                      <div className="forecast-precipitation">
                        <WiShowers size={16} />
                        <span>{day.precipitation_probability}%</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {lastUpdated && (
            <div className="weather-widget-updated">
              <span>Frissítve: {lastUpdated.toLocaleTimeString('hu-HU', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}</span>
            </div>
          )}
        </>
      ) : (
        <div className="weather-widget-placeholder">
          <FaCloudSun className="placeholder-icon" />
          <span>Időjárás adatok nem elérhetők</span>
        </div>
      )}
      </div>
    </>
  );
};

export default WeatherWidget;
